import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { Screen } from "@/components/Screen";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, Inbox as InboxIcon, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/_authenticated/inbox")({
  head: () =>
    pageMeta({
      title: "Inbox — Roava by WGP Apps",
      description: "In-app notifications delivered to you.",
      path: "/inbox",
    }),
  component: InboxPage,
});

type InboxRow = {
  id: string;
  category: string;
  trip_id: string | null;
  title: string | null;
  body: string | null;
  data: Record<string, unknown> | null;
  read_at: string | null;
  created_at: string;
};

const PAGE_SIZE = 50;

function InboxPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const q = useInfiniteQuery({
    queryKey: ["inbox"],
    initialPageParam: 0 as number,
    queryFn: async ({ pageParam }) => {
      const from = pageParam * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await supabase
        .from("notification_inbox")
        .select("id, category, trip_id, title, body, data, read_at, created_at")
        .order("created_at", { ascending: false })
        .range(from, to);
      if (error) throw new Error(error.message);
      return (data ?? []) as InboxRow[];
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === PAGE_SIZE ? allPages.length : undefined,
  });

  const rows = (q.data?.pages ?? []).flat();

  const markRead = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notification_inbox")
        .update({ read_at: new Date().toISOString() })
        .eq("id", id)
        .is("read_at", null);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("notification_inbox")
        .update({ read_at: new Date().toISOString() })
        .is("read_at", null);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox"] });
      toast.success(t("inbox.toast_marked_all"));
    },
    onError: (e: any) => toast.error(e?.message ?? t("inbox.toast_failed")),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("notification_inbox")
        .delete()
        .eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inbox"] }),
    onError: (e: any) => toast.error(e?.message ?? t("inbox.toast_failed")),
  });

  const unreadCount = rows.filter((r) => !r.read_at).length;

  return (
    <Screen
      title={t("inbox.page_title")}
      subtitle={t("inbox.page_subtitle")}
      action={
        <button
          onClick={() => nav({ to: "/notifications" })}
          aria-label={t("inbox.back")}
          className="grid place-items-center size-9 rounded-full border border-border bg-background hover:bg-accent/40"
        >
          <ChevronLeft className="size-4" />
        </button>
      }
    >
      <div className="space-y-3">
        {unreadCount > 0 && (
          <div className="flex justify-end">
            <Button
              size="sm"
              variant="ghost"
              className="rounded-xl text-xs"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              {t("inbox.btn_mark_all_read")}
            </Button>
          </div>
        )}

        {q.isLoading ? (
          <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground text-center">
            {t("inbox.loading")}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <InboxIcon className="size-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm font-medium">{t("inbox.empty_title")}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {t("inbox.empty_body")}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.id}
                onClick={() => !r.read_at && markRead.mutate(r.id)}
                className={`rounded-2xl border p-4 cursor-pointer transition ${
                  r.read_at
                    ? "border-border bg-card opacity-70"
                    : "border-primary/40 bg-primary/[0.04]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!r.read_at && (
                        <span className="size-1.5 rounded-full bg-primary" />
                      )}
                      <p className="font-medium text-sm truncate">
                        {r.title ?? r.category}
                      </p>
                    </div>
                    {r.body && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {r.body}
                      </p>
                    )}
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1.5">
                      {r.category.replace(/_/g, " ")} ·{" "}
                      {formatDistanceToNow(new Date(r.created_at), {
                        addSuffix: true,
                      })}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={t("inbox.delete_aria")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDelete(r.id);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </div>
            ))}
            {q.hasNextPage && (
              <Button
                variant="outline"
                className="w-full rounded-xl"
                onClick={() => q.fetchNextPage()}
                disabled={q.isFetchingNextPage}
              >
                {q.isFetchingNextPage ? t("inbox.btn_loading_more") : t("inbox.btn_load_more")}
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("inbox.delete_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("inbox.delete_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("inbox.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDelete) del.mutate(pendingDelete);
                setPendingDelete(null);
              }}
            >
              {t("inbox.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
